import dayjs from "dayjs";
import { getStops } from "../../../app/api/getStops";
import { postCustomBookingsSearch } from "../../../app/api/postCustomBookingsSearch";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getUserProfile } from "../../../Lib/Utils/SessionStorageUtil";
import { convertISO8601ToDuration, convertTimeToArabic, getTrainClassTypeBaseOnProduct } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedTrips, IBookingsSearchRequest, IBookingsSearchResponse, IPassenger } from "../../../Orchestrator/Types/Index";
import stopManager from "../../../Lib/Utils/StopManager";
import { getBooking } from "../../../app/api/getBooking";

class GetBookItAgainTripList extends BaseAction<void, void> {
    protected deduplicate = true;

    override async run(): Promise<void> {
        try {
            const currentDate = new Date();
            const prevYear = new Date();
            const nextYear = new Date();
            prevYear.setHours(currentDate.getHours() - 1);
            nextYear.setFullYear(currentDate.getFullYear() + 1);

            const endDate = nextYear.toISOString();
            const startTime = prevYear.toISOString();

            updateRecoilState('bookedTripsAtom', []);
            try {
                const session = await getUserProfile();
                const searchRequest: IBookingsSearchRequest = {
                    travelDateRange: {
                        startTime: startTime,
                        endDate: endDate
                    },
                    purchaserPassengerEmail: session?.email || ""
                };
                const response: any = await postCustomBookingsSearch(searchRequest, 30);

                if (response.status === 'ok' && response.answer) {
                    const bookingResults: IBookingsSearchResponse = response.answer;
                    updateRecoilState("bookingsSearchResponseAtom", bookingResults);
                    const stopResponse: any = await getStops();

                    if (stopResponse.status === "ok" && stopResponse.answer) {
                        const stops = stopResponse.answer.stops;
                        const bookedTrips: IBookedTrips[] = bookingResults.bookingSearchResults.flatMap((booking) => {
                            let bookingStatus = ""
                            const allBookedOfferSummariesWithoutExchange = booking.bookedOfferSummaries.filter(summary => {
                                const filterPartSummariesWithoutExchange = summary.bookingPartSummaries.find(summary => summary.status === "FULFILLED")
                                return filterPartSummariesWithoutExchange ? true : false
                            })

                            let noModificationOnBooking = true;

                            //Check booking is fully fulfilled
                            for (let i = 0; i < booking.bookedOfferSummaries.length; i++) {
                                const bookSummaries = booking.bookedOfferSummaries[i].bookingPartSummaries;
                                const isAnyFulfilled = bookSummaries.find(d => d.status !== "FULFILLED")
                                if (isAnyFulfilled) {
                                    noModificationOnBooking = false
                                    break;
                                }
                            }

                            //Check at least any one fulfilled
                            for (let i = 0; i < allBookedOfferSummariesWithoutExchange.length; i++) {
                                const bookSummaries = allBookedOfferSummariesWithoutExchange[i].bookingPartSummaries;
                                const isAnyFulfilled = bookSummaries.find(d => d.status === "FULFILLED")
                                if (isAnyFulfilled) {
                                    bookingStatus = "FULFILLED"
                                    break;
                                }
                            }
                            //Check all are refunded.
                            if (bookingStatus === "") {
                                for (let i = 0; i < booking.bookedOfferSummaries.length; i++) {
                                    const bookSummaries = booking.bookedOfferSummaries[i].bookingPartSummaries;
                                    const isAllRefunded = bookSummaries.filter(d => d.status === "REFUNDED")
                                    if (bookSummaries.length === isAllRefunded.length) {
                                        bookingStatus = "REFUNDED"
                                        break;
                                    }
                                }
                            }

                            // Original trip creation logic for non-split cases
                            return {
                                bookingId: booking.id,
                                fromName: stopManager.getStopNameById(booking.tripSummaries[0].origin.stopPlaceRef),
                                toName: stopManager.getStopNameById(booking.tripSummaries[0].destination.stopPlaceRef),
                                from: booking.tripSummaries[0].origin.stopPlaceRef,
                                to: booking.tripSummaries[0].destination.stopPlaceRef,
                                departureTime: convertTimeToArabic(booking.tripSummaries[0].startTime),
                                arrivalTime: convertTimeToArabic(booking.tripSummaries[0].endTime),
                                class: "",
                                summary: allBookedOfferSummariesWithoutExchange?.length > 0 ? allBookedOfferSummariesWithoutExchange[0]?.bookingPartSummaries[0]?.summary || "" : booking.bookedOfferSummaries[0].bookingPartSummaries[0]?.summary || "",
                                type: booking.passengers[0]?.type || 'Unknown',
                                passengers: booking.passengers.map((passenger: IPassenger) => ({
                                    ...passenger,
                                    detail: {
                                        ...passenger.detail,
                                        firstName: passenger.detail.firstName || '',
                                        lastName: passenger.detail.lastName || '',
                                        email: passenger.detail.email || '',
                                    },
                                })),
                                noModificationOnBooking: noModificationOnBooking,
                                duration: convertISO8601ToDuration(booking.tripSummaries[0].duration),
                                trainNumber: booking.code,
                                stops: booking.tripSummaries[0].transfers > 0 ? Array(booking.tripSummaries[0].transfers).fill('Stop') : [],
                                status: bookingStatus,
                                startDate: "",
                                ticketLink: "",
                                arrivalDateTime: booking.tripSummaries[0].endTime,
                                departureDateTime: booking.tripSummaries[0].startTime,
                                bookingType: allBookedOfferSummariesWithoutExchange?.length > 1 ? "roundTrip" : "oneWayTrip",
                                outBoundOfferID: allBookedOfferSummariesWithoutExchange[0]?.offerId ?? null,
                                inboundOfferID: allBookedOfferSummariesWithoutExchange[1]?.offerId ?? null,
                                ...(allBookedOfferSummariesWithoutExchange[1] && {
                                    inboundData: {
                                        duration: booking.tripSummaries.length > 1 && booking.tripSummaries[1]?.duration ? convertISO8601ToDuration(booking.tripSummaries[1].duration) : "",
                                        stops: booking.tripSummaries[1]?.transfers > 0 ? Array(booking.tripSummaries[1]?.transfers).fill('Stop') : [],
                                        departureTime: convertTimeToArabic(booking.tripSummaries[1]?.startTime),
                                        arrivalTime: convertTimeToArabic(booking.tripSummaries[1]?.endTime),
                                        arrivalDateTime: booking.tripSummaries[1]?.endTime,
                                        departureDateTime: booking.tripSummaries[1]?.startTime,
                                        summary: allBookedOfferSummariesWithoutExchange?.length > 0 ? allBookedOfferSummariesWithoutExchange[1]?.bookingPartSummaries[0]?.summary || "" : booking.bookedOfferSummaries[1].bookingPartSummaries[0]?.summary || "",
                                    }
                                }),
                            };
                        });
                        /**
                        * This is for book it again section                                
                        * Need to show only those booking which is not modified.
                        * Need to show only fulfilled booking
                        * Need to show only future booking no past booking
                        * Need to show only 9 booking
                        * Need to show only unique trip no duplicate
                        * */
                        const onlyFulfilledBooking = bookedTrips.filter(d =>
                            d.noModificationOnBooking &&
                            d.status === "FULFILLED" &&
                            dayjs(d.departureDateTime).tz("Asia/Dubai") > dayjs().tz("Asia/Dubai")
                        ).slice(0, 9)

                        const uniqueBookingsMap: Map<string, IBookedTrips> = new Map();
                        for (const booking of onlyFulfilledBooking as IBookedTrips[]) {
                            const origin = booking.to;
                            const destination = booking.from;
                            const startTime = booking.departureDateTime;
                            const endTime = booking.arrivalDateTime;
                            let tripSegmentsKey = `${origin}->${destination}@${startTime}-${endTime}`;

                            const passengerConfig = booking.passengers.map((p) => `${p.type}-${p.age}`)
                            const key = `${tripSegmentsKey}|${passengerConfig.join(",")}`;

                            const existing = uniqueBookingsMap.get(key);
                            if (!existing) {
                                uniqueBookingsMap.set(key, booking);
                            }
                        }

                        const uniqueList: IBookedTrips[] = Array.from(uniqueBookingsMap.values());
                        let bookingTrips: IBookedTrips[] = []
                        for (const booking of [...uniqueList].splice(0, 3) as IBookedTrips[]) {
                            const bookingDetails = await getBooking(booking.bookingId, false)
                            if (bookingDetails.status === "ok" && bookingDetails.answer) {
                                let outboundOfferLegDetail = bookingDetails.answer.booking.bookedOffers.find(i => i.offerId === booking.outBoundOfferID)

                                bookingTrips.push({
                                    ...booking,
                                    selectedPriceClass: getTrainClassTypeBaseOnProduct(outboundOfferLegDetail?.products || []) || "",
                                })
                            }
                        }

                        updateRecoilState('bookedTripsAtom', bookingTrips);
                    }
                } else {
                    console.error("No results found or response is not ok:", response);
                }
            } catch (error) {
                console.error("Error searching for bookings:", error);
            }
        } catch (error: any) {
            console.error("Error fetching stops:", error);
        }
    }
}

export const getBookItAgainTripList = new GetBookItAgainTripList()


